/**
 * PublicShopBrowser — Unified browse page for all public shops (no auth required).
 * Route: /browse
 *
 * Merges the old PublicShopBrowser (search, filters, categories) with
 * ShopSelector's card design (full-bleed image, blur overlay, preview animation).
 *
 * Data sources:
 *   - /api/shops/public  → shop info (name, address, category, sections)
 *   - /api/datasets       → 3D scan data (camera count, paths for preview animation)
 *   Joined on Dataset.shopId === Shop.id
 *
 * Features:
 *   - Search by name / address
 *   - Category filter chips
 *   - Full-bleed image cards with glassmorphism info overlay
 *   - Preview animation on hover/scroll (cross-fade frame sequence)
 *   - Camera count badge
 *   - SPA navigation to 3D viewer via setCurrentShop()
 *   - "View on Map" toggle → /map
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useShopStore, getEnrichedShops, type EnrichedPublicShop } from '../../stores/shopStore'
import { useAppStore } from '@store/appStore'
import { useShopCardPreview } from '@hooks/useShopCardPreview'
import type { ShopMetadata } from '@app-types/shopMetadata'
import { apiUrl, resolveUrl, toBlobUrl } from '../../config'
import './PublicShopBrowser.css'

const CATEGORIES = ['Grocery', 'Electronics', 'Clothing', 'Restaurant', 'Pharmacy', 'Supermarket', 'Cafe', 'Bakery']

export default function PublicShopBrowser() {
  const navigate = useNavigate()
  const { t } = useTranslation('shops')
  const { publicShops, publicShopsLoading, publicDatasetsLoading, publicDatasets, fetchPublicShops, fetchPublicDatasets } = useShopStore()
  const setAvailableDatasets = useAppStore((state) => state.setAvailableDatasets)
  const setAvailableShops = useAppStore((state) => state.setAvailableShops)
  const setCurrentShop = useAppStore((state) => state.setCurrentShop)
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  // Track searches (debounced — fires 1s after user stops typing)
  useEffect(() => {
    const q = search.trim()
    if (!q || q.length < 2) return
    const timer = setTimeout(() => {
      import('../../services/activityService').then(({ trackSearch }) => {
        trackSearch(q)
      })
    }, 1000)
    return () => clearTimeout(timer)
  }, [search])

  // Shop metadata for preview animation
  const [shopMetadata, setShopMetadata] = useState<Map<string, ShopMetadata>>(new Map())
  const metadataLoadedRef = useRef<Set<string>>(new Set())

  // Preview animation hook
  const { previewFrame, previousFrame, previewEffects, isPreviewing, currentShopRef, startPreview, stopPreview } = useShopCardPreview()

  // Hover timeout refs for delayed stop
  const hoverTimeoutRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const shopRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const currentCenterShopRef = useRef<string | null>(null)

  // Fetch both data sources on mount
  useEffect(() => {
    fetchPublicShops()
    fetchPublicDatasets().then(() => {
      // Also populate appStore so useShopCardPreview can find datasets
      const { publicDatasets } = useShopStore.getState()
      setAvailableDatasets(publicDatasets)
      const shopIds = publicDatasets.map(d => d.id)
      setAvailableShops(shopIds)
    })
  }, [])

  // Get enriched shops (joined data)
  const enrichedShops = useMemo(() => getEnrichedShops(), [publicShops, publicDatasets])

  // Client-side filter for instant feedback
  const filtered = useMemo(() => {
    let list = enrichedShops
    if (selectedCategory) {
      list = list.filter((s) => s.category?.toLowerCase() === selectedCategory.toLowerCase())
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter(
        (s) => s.name.toLowerCase().includes(q) || (s.address && s.address.toLowerCase().includes(q))
      )
    }
    return list
  }, [enrichedShops, search, selectedCategory])

  // Extract unique categories from actual data for chips
  const availableCategories = useMemo(() => {
    const cats = new Set(publicShops.map((s) => s.category).filter(Boolean))
    return CATEGORIES.filter((c) => cats.has(c)).concat(
      [...cats].filter((c) => !CATEGORIES.includes(c))
    )
  }, [publicShops])

  // Load shop metadata lazily for visible cards (for preview animation)
  const loadMetadataForShop = useCallback(async (shop: EnrichedPublicShop) => {
    if (!shop.dataset || metadataLoadedRef.current.has(shop.id)) return
    metadataLoadedRef.current.add(shop.id)
    try {
      const metadataPath = shop.dataset.paths?.metadata || `/api/scan-data/${shop.dataset.id}/shop_metadata.json`
      const response = await fetch(toBlobUrl(resolveUrl(metadataPath)))
      if (response.ok) {
        const metadata: ShopMetadata = await response.json()
        setShopMetadata(prev => new Map(prev).set(shop.dataset!.id, metadata))
      }
    } catch (error) {
      console.warn(`[PublicShopBrowser] Failed to load metadata for ${shop.id}:`, error)
    }
  }, [])

  // IntersectionObserver to lazy-load metadata for visible cards
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const shopId = entry.target.getAttribute('data-shop-id')
            if (shopId) {
              const shop = enrichedShops.find(s => s.id === shopId)
              if (shop) loadMetadataForShop(shop)
            }
          }
        }
      },
      { rootMargin: '200px' }
    )
    shopRefs.current.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [enrichedShops, loadMetadataForShop])

  // Scroll-based auto-play: start preview for card closest to viewport center
  useEffect(() => {
    if (shopRefs.current.size === 0) return

    const findCardInCenter = (): string | null => {
      const viewportCenter = window.innerHeight / 2
      let closestId: string | null = null
      let closestDist = Infinity
      shopRefs.current.forEach((el, id) => {
        const rect = el.getBoundingClientRect()
        const dist = Math.abs(rect.top + rect.height / 2 - viewportCenter)
        if (dist < closestDist) { closestDist = dist; closestId = id }
      })
      return closestId
    }

    const handleScroll = () => {
      const centerShop = findCardInCenter()
      if (centerShop === currentCenterShopRef.current) return
      currentCenterShopRef.current = centerShop
      if (centerShop) {
        const shop = enrichedShops.find(s => s.id === centerShop)
        const datasetId = shop?.dataset?.id
        if (datasetId && shopMetadata.has(datasetId)) {
          startPreview(datasetId, shopMetadata)
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [enrichedShops, shopMetadata, startPreview])

  const handleShopClick = useCallback((shop: EnrichedPublicShop) => {
    if (shop.has3DViewer && shop.dataset) {
      // SPA navigation: set current shop and navigate to viewer
      setCurrentShop(shop.dataset.id)
      window.location.href = `/?shopid=${shop.dataset.id}`
    } else {
      // No 3D viewer — go to the public shop detail page
      navigate(`/shop/${shop.id}`)
    }
  }, [setCurrentShop, navigate])

  const isLoading = publicShopsLoading || publicDatasetsLoading

  return (
    <div className="shop-browser">
      {/* Header */}
      <header className="shop-browser-header">
        <button className="shop-browser-home" onClick={() => navigate('/')}>
          🏠
        </button>
        <h1>{t('browseShops')}</h1>
        <button className="shop-browser-map-btn" onClick={() => navigate('/map')}>
          🗺️
        </button>
      </header>

      {/* Search */}
      <div className="shop-browser-search-bar">
        <input
          type="text"
          placeholder={t('searchShopsPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="shop-browser-search"
        />
      </div>

      {/* Category chips */}
      {availableCategories.length > 0 && (
        <div className="shop-browser-chips">
          <button
            className={`shop-browser-chip ${!selectedCategory ? 'active' : ''}`}
            onClick={() => setSelectedCategory(null)}
          >
            {t('common:all')}
          </button>
          {availableCategories.map((cat) => (
            <button
              key={cat}
              className={`shop-browser-chip ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Results */}
      <div className="shop-browser-body">
        {isLoading ? (
          <div className="shop-browser-loading">
            <div className="shop-browser-spinner" />
            {t('loadingShops')}
          </div>
        ) : filtered.length === 0 ? (
          <div className="shop-browser-empty">
            {publicShops.length === 0 ? t('noPublicShopsYet') : t('noShopsMatchSearch')}
          </div>
        ) : (
          <div className="shop-browser-grid">
            {filtered.map((shop) => (
              <ShopCard
                key={shop.id}
                shop={shop}
                shopRefs={shopRefs}
                isPreviewing={isPreviewing}
                previewFrame={previewFrame}
                previousFrame={previousFrame}
                previewEffects={previewEffects}
                currentPreviewShop={currentShopRef.current}
                shopMetadata={shopMetadata}
                hoverTimeoutRef={hoverTimeoutRef}
                startPreview={startPreview}
                stopPreview={stopPreview}
                onClick={() => handleShopClick(shop)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/* ── ShopCard: Full-bleed image with glassmorphism info overlay ── */

interface ShopCardProps {
  shop: EnrichedPublicShop
  shopRefs: React.MutableRefObject<Map<string, HTMLDivElement>>
  isPreviewing: boolean
  previewFrame: string | null
  previousFrame: string | null
  previewEffects: { currentOpacity?: number; previousOpacity?: number }
  currentPreviewShop: string | null
  shopMetadata: Map<string, ShopMetadata>
  hoverTimeoutRef: React.MutableRefObject<Map<string, ReturnType<typeof setTimeout>>>
  startPreview: (shopId: string, metadata: Map<string, ShopMetadata>) => void
  stopPreview: () => void
  onClick: () => void
}

function ShopCard({
  shop, shopRefs, isPreviewing, previewFrame, previousFrame,
  previewEffects, currentPreviewShop, shopMetadata, hoverTimeoutRef,
  startPreview, stopPreview, onClick,
}: ShopCardProps) {
  const { t } = useTranslation('shops')
  const posterUrl = apiUrl(`/api/shops/${shop.id}/poster`)
  const [imgError, setImgError] = useState(false)
  const datasetId = shop.dataset?.id

  // Thumbnail error fallback chain: poster → thumbnail.jpg → frame_0000.jpg → placeholder
  const handleThumbnailError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    const currentSrc = img.src
    if (currentSrc.includes('/poster') && shop.dataset) {
      const basePath = shop.dataset.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shop.dataset.id}`
      img.src = toBlobUrl(resolveUrl(`${basePath}/thumbnail.jpg?shop=${shop.dataset.id}`))
    } else if (currentSrc.includes('thumbnail.jpg') && shop.dataset) {
      const basePath = shop.dataset.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shop.dataset.id}`
      img.src = toBlobUrl(resolveUrl(`${basePath}/section_0/frames/frame_0000.jpg`))
    } else {
      setImgError(true)
    }
  }

  const isThisCardPreviewing = isPreviewing && datasetId && currentPreviewShop === datasetId

  return (
    <div
      ref={(el) => {
        if (el) shopRefs.current.set(shop.id, el)
      }}
      className="shop-card"
      data-shop-id={shop.id}
      onClick={onClick}
      onMouseEnter={() => {
        if (!datasetId) return
        const existingTimeout = hoverTimeoutRef.current.get(datasetId)
        if (existingTimeout) clearTimeout(existingTimeout)
        if (shopMetadata.has(datasetId)) {
          startPreview(datasetId, shopMetadata)
        }
      }}
      onMouseLeave={() => {
        if (!datasetId) return
        const timeout = setTimeout(() => {
          stopPreview()
          hoverTimeoutRef.current.delete(datasetId)
        }, 500)
        hoverTimeoutRef.current.set(datasetId, timeout)
      }}
      role="button"
      tabIndex={0}
      aria-label={`${shop.name}${shop.cameraCount ? ` — ${shop.cameraCount} cameras` : ''}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() }
      }}
    >
      {/* Full-bleed background image */}
      <div className="shop-card-thumbnail-container">
        {!imgError ? (
          <img
            className="shop-card-img"
            src={posterUrl}
            alt={`${shop.name} thumbnail`}
            loading="lazy"
            onError={handleThumbnailError}
          />
        ) : (
          <div className="shop-card-img-placeholder">🏪</div>
        )}

        {/* Preview animation overlay (cross-fade between frames) */}
        {isThisCardPreviewing && (
          <>
            {previousFrame && (
              <img
                className="shop-card-preview-frame"
                src={previousFrame}
                alt=""
                style={{ opacity: previewEffects.previousOpacity ?? 0, zIndex: 10 }}
              />
            )}
            {previewFrame && (
              <img
                className="shop-card-preview-frame"
                src={previewFrame}
                alt=""
                style={{ opacity: previewEffects.currentOpacity ?? 1, zIndex: 11 }}
              />
            )}
          </>
        )}

        {/* Gradient overlay for text readability */}
        <div className="shop-card-overlay" />

        {/* Camera count badge */}
        {shop.cameraCount != null && (
          <div className="shop-card-badge">
            <svg className="shop-card-badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>{shop.cameraCount}</span>
          </div>
        )}

        {/* "Coming soon" indicator for shops without 3D data */}
        {!shop.has3DViewer && (
          <div className="shop-card-coming-soon">{t('comingSoon')}</div>
        )}
      </div>

      {/* Info overlay (blurred background at bottom) */}
      <div className="shop-card-info">
        <div className="shop-card-name">{shop.name}</div>
        <div className="shop-card-meta">
          {shop.category && <span className="shop-card-category">{shop.category}</span>}
          <span className="shop-card-sections">
            {t('sectionCount', { count: shop.sections?.length || 0 })}
          </span>
        </div>
      </div>
    </div>
  )
}
